/* YouTube loads only after a deliberate click. No API key or channel scraping. */
if (!customElements.get('mq-youtube-shorts')) {
  customElements.define('mq-youtube-shorts', class MqYoutubeShorts extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.mqs-track');
      this.cards = [...this.querySelectorAll('[data-short]')];
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      this.cards.forEach(card => {
        const id = this.videoId(card.dataset.url);
        const button = card.querySelector('[data-play]');
        if (!id) {
          card.querySelector('[data-error]').hidden = false;
          return;
        }
        card.dataset.videoId = id;
        button.disabled = false;
        const image = card.querySelector('[data-thumbnail]');
        if (image) {
          image.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
          image.hidden = false;
        }
        const link = card.querySelector('[data-youtube]');
        link.href = `https://www.youtube.com/shorts/${id}`;
        link.hidden = false;
        button.addEventListener('click', () => this.play(card), options);
      });
      this.previous?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.refresh());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      document.addEventListener('mq:short-play', event => {
        this.cards.forEach(card => { if (card !== event.detail.card) this.stop(card); });
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.cards.forEach(card => this.stop(card));
      }, options);
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting) this.stop(entry.target); });
      });
      this.cards.forEach(card => this.visibility.observe(card));
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.block === event.detail.blockId);
        if (card) this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'instant' });
      }, options);
      this.resize = new ResizeObserver(() => this.refresh());
      this.resize.observe(this.track);
      this.refresh();
    }

    videoId(value) {
      const raw = (value || '').trim();
      if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;
      try {
        const url = new URL(raw);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        const host = url.hostname.toLowerCase();
        let id;
        if (host === 'youtu.be') id = url.pathname.split('/')[1];
        else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
          const parts = url.pathname.split('/').filter(Boolean);
          id = ['shorts', 'embed'].includes(parts[0]) ? parts[1] : url.searchParams.get('v');
        }
        return /^[a-zA-Z0-9_-]{11}$/.test(id || '') ? id : null;
      } catch { return null; }
    }

    play(card) {
      if (card.querySelector('iframe') || !card.dataset.videoId) return;
      document.dispatchEvent(new CustomEvent('mq:short-play', { detail: { card } }));
      const player = document.createElement('iframe');
      player.src = `https://www.youtube-nocookie.com/embed/${card.dataset.videoId}?autoplay=1&playsinline=1&controls=1&rel=0&hl=es&cc_load_policy=1&cc_lang_pref=es`;
      player.title = card.dataset.title;
      player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      player.allowFullscreen = true;
      player.referrerPolicy = 'strict-origin-when-cross-origin';
      const button = card.querySelector('[data-play]');
      button.hidden = true;
      button.setAttribute('aria-expanded', 'true');
      card.querySelector('.mqs-media').appendChild(player);
      player.focus();
    }

    stop(card) {
      const player = card.querySelector('iframe');
      if (!player) return;
      player.remove();
      const button = card.querySelector('[data-play]');
      button.hidden = false;
      button.setAttribute('aria-expanded', 'false');
    }

    move(direction) {
      if (!this.cards.length) return;
      const step = this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.track.clientWidth;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * step, behavior: reduced ? 'instant' : 'smooth' });
    }

    refresh() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const controls = this.querySelector('.mqs-controls');
      if (controls) controls.hidden = max <= 3;
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 3;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 3;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      cancelAnimationFrame(this.frame);
      this.cards?.forEach(card => this.stop(card));
    }
  });
}
